import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory2';

import SBDropdown from '../../../components/Dropdown';
import SbTextField from '../../../components/Textfield';
import Contact from '@Components/Contact';

import { ShipmentUserSchema } from './Schema';
import { productDescriptionSchema } from '../ShipmentDetails/ProductDescription/schema';
import { SHIPMENT_MODES, SHIPMENT_UNITS, DUTY_CHARGES } from '../constants';
import { DropdownOptionType } from '../../../components/Dropdown/Type';
import { isDutyChargesAllowedSubdomain } from '../../../utils/getSubdomain';

interface EditBulkShipmentModalProps {
  shipment: any;
  onClose: () => void;
  onSave: (updated: any) => void;
  countryList: DropdownOptionType[];
  currencyList: DropdownOptionType[];
}

export default function EditBulkShipmentModal({
  shipment,
  onClose,
  onSave,
  countryList,
  currencyList,
}: EditBulkShipmentModalProps) {
  const [tabIndex, setTabIndex] = useState(0);
  const [edited, setEdited] = useState(() => JSON.parse(JSON.stringify(shipment)));

  // Validate address using Joi
  const validateAddress = (addr: any) => {
    const cleanAddr = {
      ...addr,
      phoneNumber: addr.phoneNumber ? Number(addr.phoneNumber) : undefined,
      telephoneCode: addr.telephoneCode ? Number(addr.telephoneCode) : undefined,
    };
    const { error } = ShipmentUserSchema.validate(cleanAddr, { abortEarly: false });
    if (!error) return {};
    const errObj: { [key: string]: string } = {};
    error.details.forEach(d => {
      const key = d.path[0] as string;
      errObj[key] = d.message;
    });
    return errObj;
  };

  // Validate product using Joi
  const validateProduct = (prod: any) => {
    const cleanProd = {
      ...prod,
      quantity: prod.quantity ? Number(prod.quantity) : undefined,
      unitValue: prod.unitValue ? Number(prod.unitValue) : undefined,
    };
    const { error } = productDescriptionSchema.validate(cleanProd, { abortEarly: false });
    if (!error) return {};
    const errObj: { [key: string]: string } = {};
    error.details.forEach(d => {
      const key = d.path[0] as string;
      errObj[key] = d.message;
    });
    return errObj;
  };

  const shipperErrors = validateAddress(edited.shipper);
  const consigneeErrors = validateAddress(edited.consignee);

  const isExport = edited.shipper?.country?.toLowerCase() !== edited.consignee?.country?.toLowerCase();

  /** Strip everything except digits from a phone number string */
  const sanitizePhoneNumber = (value: string): string => value.replace(/[^\d]/g, '');

  const handleShipperChange = (field: string, value: any) => {
    const sanitized = field === 'phoneNumber' ? sanitizePhoneNumber(String(value)) : value;
    setEdited((prev: any) => ({
      ...prev,
      shipper: { ...prev.shipper, [field]: sanitized },
    }));
  };

  const handleConsigneeChange = (field: string, value: any) => {
    const sanitized = field === 'phoneNumber' ? sanitizePhoneNumber(String(value)) : value;
    setEdited((prev: any) => ({
      ...prev,
      consignee: { ...prev.consignee, [field]: sanitized },
    }));
  };

  const handlePackageChange = (field: string, value: any) => {
    setEdited((prev: any) => ({
      ...prev,
      package: { ...prev.package, [field]: value },
    }));
  };

  const handleBoxChange = (boxIndex: number, field: string, value: any) => {
    setEdited((prev: any) => {
      const boxCopy = [...prev.package.box];
      boxCopy[boxIndex] = { ...boxCopy[boxIndex], [field]: value };

      // Update totalBoxWeight
      const totalBoxWeight = boxCopy.reduce((prevW, currW) => prevW + (parseFloat(currW.weight) || 0), 0);

      return {
        ...prev,
        package: {
          ...prev.package,
          box: boxCopy,
          totalBoxWeight,
        },
      };
    });
  };

  const handleProductChange = (boxIndex: number, prodIndex: number, field: string, value: any) => {
    setEdited((prev: any) => {
      const boxCopy = [...prev.package.box];
      const prodCopy = [...boxCopy[boxIndex].products];

      prodCopy[prodIndex] = { ...prodCopy[prodIndex], [field]: value };

      // Recalculate total value
      const qty = Number(prodCopy[prodIndex].quantity) || 0;
      const val = Number(prodCopy[prodIndex].unitValue) || 0;
      prodCopy[prodIndex].totalValue = qty * val;

      boxCopy[boxIndex] = { ...boxCopy[boxIndex], products: prodCopy };
      return {
        ...prev,
        package: {
          ...prev.package,
          box: boxCopy,
        },
      };
    });
  };

  const addBox = () => {
    setEdited((prev: any) => {
      const boxCopy = [...prev.package.box];
      boxCopy.push({
        id: boxCopy.length.toString(),
        length: '',
        width: '',
        height: '',
        weight: '',
        boxCopyCount: 0,
        products: [],
      });
      return {
        ...prev,
        package: { ...prev.package, box: boxCopy },
      };
    });
  };

  const removeBox = (boxIndex: number) => {
    setEdited((prev: any) => {
      const boxCopy = prev.package.box.filter((_: any, i: number) => i !== boxIndex);
      const reindexed = boxCopy.map((box: any, idx: number) => ({
        ...box,
        id: idx.toString(),
      }));
      return {
        ...prev,
        package: { ...prev.package, box: reindexed },
      };
    });
  };

  const addProduct = (boxIndex: number) => {
    setEdited((prev: any) => {
      const boxCopy = [...prev.package.box];
      const prodCopy = [...boxCopy[boxIndex].products];
      prodCopy.push({
        id: prodCopy.length.toString(),
        description: '',
        quantity: 1,
        unitValue: 1,
        HSNName: '',
        HSNCode: '',
        totalValue: 1,
      });
      boxCopy[boxIndex] = { ...boxCopy[boxIndex], products: prodCopy };
      return {
        ...prev,
        package: { ...prev.package, box: boxCopy },
      };
    });
  };

  const removeProduct = (boxIndex: number, prodIndex: number) => {
    setEdited((prev: any) => {
      const boxCopy = [...prev.package.box];
      const prodCopy = boxCopy[boxIndex].products.filter((_: any, i: number) => i !== prodIndex);
      const reindexed = prodCopy.map((p: any, idx: number) => ({
        ...p,
        id: idx.toString(),
      }));
      boxCopy[boxIndex] = { ...boxCopy[boxIndex], products: reindexed };
      return {
        ...prev,
        package: { ...prev.package, box: boxCopy },
      };
    });
  };

  const handleSaveClick = () => {
    onSave(edited);
  };

  const theme = useTheme();

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          px: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <AutoAwesomeIcon sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: '1.15rem' }}>
            Edit Shipment Details
          </Typography>
          {edited.package?.invoiceNumber && (
            <Chip
              label={edited.package.invoiceNumber}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 700, fontSize: '0.75rem', height: '24px' }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: theme.palette.background.paper }}>
        <Tabs
          value={tabIndex}
          onChange={(_, val) => setTabIndex(val)}
          aria-label="shipment edit tabs"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              minHeight: '48px',
            },
          }}
        >
          <Tab
            icon={<PersonIcon sx={{ fontSize: '18px !important' }} />}
            iconPosition="start"
            label="Shipper Address"
          />
          <Tab
            icon={<LocalShippingIcon sx={{ fontSize: '18px !important' }} />}
            iconPosition="start"
            label="Consignee Address"
          />
          <Tab
            icon={<InventoryIcon sx={{ fontSize: '18px !important' }} />}
            iconPosition="start"
            label="Package & Boxes"
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 3, maxHeight: '65vh', overflowY: 'auto' }}>
        {/* Shipper Tab */}
        {tabIndex === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#666' }}>
                Shipper Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SbTextField
                placeholder="Enter Name"
                label="Shipper Name"
                required
                name="name"
                value={edited.shipper?.name || ''}
                onChange={e => handleShipperChange('name', e.target.value)}
                error={shipperErrors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SbTextField
                placeholder="Enter Company Name"
                label="Company Name"
                name="companyName"
                value={edited.shipper?.companyName || ''}
                onChange={e => handleShipperChange('companyName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Contact
                label="Mobile No. *"
                phoneName="phoneNumber"
                phoneNumber={edited.shipper?.phoneNumber || ''}
                onChangePhoneNumber={e => handleShipperChange('phoneNumber', e.target.value)}
                telCountryCodeName="telephoneCode"
                telCountryCode={edited.shipper?.telephoneCode || ''}
                onChangeTelCountryCode={event => handleShipperChange('telephoneCode', event.target.value as string)}
                setDefaultTelephone={value => handleShipperChange('telephoneCode', value.toString())}
                error={shipperErrors.phoneNumber || shipperErrors.telephoneCode}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SbTextField
                placeholder="Enter Email Id"
                label="Email Address"
                name="email"
                value={edited.shipper?.email || ''}
                onChange={e => handleShipperChange('email', e.target.value)}
                error={shipperErrors.email || shipperErrors.emailFormat}
              />
            </Grid>
            <Grid item xs={12}>
              <SbTextField
                placeholder="Line 1"
                label="Address Line 1"
                required
                name="line1"
                value={edited.shipper?.line1 || ''}
                onChange={e => handleShipperChange('line1', e.target.value)}
                error={shipperErrors.line1}
              />
            </Grid>
            <Grid item xs={12}>
              <SbTextField
                placeholder="Line 2"
                label="Address Line 2"
                name="line2"
                value={edited.shipper?.line2 || ''}
                onChange={e => handleShipperChange('line2', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SbTextField
                placeholder="City"
                label="City"
                required
                name="city"
                value={edited.shipper?.city || ''}
                onChange={e => handleShipperChange('city', e.target.value)}
                error={shipperErrors.city}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SbTextField
                placeholder="State"
                label="State"
                name="state"
                value={edited.shipper?.state || ''}
                onChange={e => handleShipperChange('state', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SbTextField
                placeholder="Pincode"
                label="Pincode"
                required
                name="pincode"
                value={edited.shipper?.pincode || ''}
                onChange={e => handleShipperChange('pincode', e.target.value)}
                error={shipperErrors.pincode}
              />
            </Grid>
            <Grid item xs={12}>
              <SBDropdown
                id="shipper-country-id"
                label="Country"
                required
                name="country"
                placeholder="Country"
                options={countryList}
                value={edited.shipper?.country ? [edited.shipper.country] : []}
                onChange={selected => handleShipperChange('country', selected[0] || '')}
                error={shipperErrors.country}
              />
            </Grid>
          </Grid>
        )}

        {/* Consignee Tab */}
        {tabIndex === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#666' }}>
                Consignee Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SbTextField
                placeholder="Enter Name"
                label="Consignee Name"
                required
                name="name"
                value={edited.consignee?.name || ''}
                onChange={e => handleConsigneeChange('name', e.target.value)}
                error={consigneeErrors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SbTextField
                placeholder="Enter Company Name"
                label="Company Name"
                name="companyName"
                value={edited.consignee?.companyName || ''}
                onChange={e => handleConsigneeChange('companyName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Contact
                label="Mobile No. *"
                phoneName="phoneNumber"
                phoneNumber={edited.consignee?.phoneNumber || ''}
                onChangePhoneNumber={e => handleConsigneeChange('phoneNumber', e.target.value)}
                telCountryCodeName="telephoneCode"
                telCountryCode={edited.consignee?.telephoneCode || ''}
                onChangeTelCountryCode={event => handleConsigneeChange('telephoneCode', event.target.value as string)}
                setDefaultTelephone={value => handleConsigneeChange('telephoneCode', value.toString())}
                error={consigneeErrors.phoneNumber || consigneeErrors.telephoneCode}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SbTextField
                placeholder="Enter Email Id"
                label="Email Address"
                name="email"
                value={edited.consignee?.email || ''}
                onChange={e => handleConsigneeChange('email', e.target.value)}
                error={consigneeErrors.email || consigneeErrors.emailFormat}
              />
            </Grid>
            <Grid item xs={12}>
              <SbTextField
                placeholder="Line 1"
                label="Address Line 1"
                required
                name="line1"
                value={edited.consignee?.line1 || ''}
                onChange={e => handleConsigneeChange('line1', e.target.value)}
                error={consigneeErrors.line1}
              />
            </Grid>
            <Grid item xs={12}>
              <SbTextField
                placeholder="Line 2"
                label="Address Line 2"
                name="line2"
                value={edited.consignee?.line2 || ''}
                onChange={e => handleConsigneeChange('line2', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SbTextField
                placeholder="City"
                label="City"
                required
                name="city"
                value={edited.consignee?.city || ''}
                onChange={e => handleConsigneeChange('city', e.target.value)}
                error={consigneeErrors.city}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SbTextField
                placeholder="State"
                label="State"
                name="state"
                value={edited.consignee?.state || ''}
                onChange={e => handleConsigneeChange('state', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SbTextField
                placeholder="Pincode"
                label="Pincode"
                required
                name="pincode"
                value={edited.consignee?.pincode || ''}
                onChange={e => handleConsigneeChange('pincode', e.target.value)}
                error={consigneeErrors.pincode}
              />
            </Grid>
            <Grid item xs={12}>
              <SBDropdown
                id="consignee-country-id"
                label="Country"
                required
                name="country"
                placeholder="Country"
                options={countryList}
                value={edited.consignee?.country ? [edited.consignee.country] : []}
                onChange={selected => handleConsigneeChange('country', selected[0] || '')}
                error={consigneeErrors.country}
              />
            </Grid>
          </Grid>
        )}

        {/* Package & Boxes Tab */}
        {tabIndex === 2 && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: '#666' }}>
              Package Parameters
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <SBDropdown
                  id="unitOfMeasurement"
                  label="Unit of Measurement"
                  required
                  name="unitOfMeasurement"
                  placeholder="Select Unit"
                  options={SHIPMENT_UNITS}
                  value={edited.package?.unitOfMeasurement ? [edited.package.unitOfMeasurement] : []}
                  onChange={selected => handlePackageChange('unitOfMeasurement', selected[0] || '')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SBDropdown
                  id="currency"
                  label="Currency"
                  required
                  name="currency"
                  placeholder="Select Currency"
                  options={currencyList}
                  value={edited.package?.currency ? [edited.package.currency] : []}
                  onChange={selected => handlePackageChange('currency', selected[0] || '')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SBDropdown
                  id="shipmentMode"
                  label="Shipment Mode"
                  required
                  name="shipmentMode"
                  placeholder="Select Mode"
                  options={SHIPMENT_MODES}
                  value={edited.package?.shipmentMode ? [edited.package.shipmentMode] : []}
                  onChange={selected => handlePackageChange('shipmentMode', selected[0] || '')}
                />
              </Grid>
              {isDutyChargesAllowedSubdomain() && (
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    id="dutyCharge"
                    label="Duty Charge"
                    required={isExport}
                    name="dutyCharge"
                    placeholder="Select Duty Charge"
                    options={DUTY_CHARGES}
                    value={
                      edited.package?.dutyCharge
                        ? [
                            typeof edited.package.dutyCharge === 'string'
                              ? edited.package.dutyCharge
                              : edited.package.dutyCharge.type || '',
                          ]
                        : []
                    }
                    onChange={selected => handlePackageChange('dutyCharge', selected[0] || '')}
                    disabled={!isExport}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <SbTextField
                  placeholder="Invoice Number"
                  label="Invoice Number"
                  name="invoiceNumber"
                  value={edited.package?.invoiceNumber || ''}
                  onChange={e => handlePackageChange('invoiceNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SbTextField
                  placeholder="Reference Number"
                  label="Reference Number"
                  name="userReferenceNumber"
                  value={edited.package?.userReferenceNumber || ''}
                  onChange={e => handlePackageChange('userReferenceNumber', e.target.value)}
                />
              </Grid>
            </Grid>

            {/* Packaging and Insurance */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!edited.package?.packaging}
                      onChange={e => handlePackageChange('packaging', e.target.checked)}
                    />
                  }
                  label="Add Packaging"
                />
              </Grid>
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!edited.package?.insurance}
                      onChange={e => handlePackageChange('insurance', e.target.checked)}
                    />
                  }
                  label="Insure Your Shipment"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666' }}>
                Boxes & Dimensions
              </Typography>
              <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addBox}>
                Add Box
              </Button>
            </Box>

            {edited.package?.box?.map((box: any, boxIdx: number) => {
              const boxLengthErr = isNaN(parseFloat(box.length)) || parseFloat(box.length) <= 0;
              const boxWidthErr = isNaN(parseFloat(box.width)) || parseFloat(box.width) <= 0;
              const boxHeightErr = isNaN(parseFloat(box.height)) || parseFloat(box.height) <= 0;
              const boxWeightErr = isNaN(parseFloat(box.weight)) || parseFloat(box.weight) <= 0;

              return (
                <Box
                  key={box.id || boxIdx}
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    p: 2,
                    mb: 3,
                    bgcolor: '#fafafa',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333' }}>
                      Box {boxIdx + 1}
                    </Typography>
                    {edited.package.box.length > 1 && (
                      <IconButton color="error" size="small" onClick={() => removeBox(boxIdx)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <SbTextField
                        placeholder="Length"
                        label="Length"
                        required
                        type="number"
                        name="length"
                        value={box.length || ''}
                        onChange={e => handleBoxChange(boxIdx, 'length', e.target.value)}
                        error={boxLengthErr ? 'Required > 0' : ''}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <SbTextField
                        placeholder="Width"
                        label="Width"
                        required
                        type="number"
                        name="width"
                        value={box.width || ''}
                        onChange={e => handleBoxChange(boxIdx, 'width', e.target.value)}
                        error={boxWidthErr ? 'Required > 0' : ''}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <SbTextField
                        placeholder="Height"
                        label="Height"
                        required
                        type="number"
                        name="height"
                        value={box.height || ''}
                        onChange={e => handleBoxChange(boxIdx, 'height', e.target.value)}
                        error={boxHeightErr ? 'Required > 0' : ''}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <SbTextField
                        placeholder="Weight"
                        label="Weight"
                        required
                        type="number"
                        name="weight"
                        value={box.weight || ''}
                        onChange={e => handleBoxChange(boxIdx, 'weight', e.target.value)}
                        error={boxWeightErr ? 'Required > 0' : ''}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#555' }}>
                      Products in Box {boxIdx + 1}
                    </Typography>
                    <Button startIcon={<AddIcon />} variant="text" size="small" onClick={() => addProduct(boxIdx)}>
                      Add Product
                    </Button>
                  </Box>

                  {box.products?.map((prod: any, prodIdx: number) => {
                    const prodErrors = validateProduct(prod);
                    const isProdDescErr = !!prodErrors.description;
                    const isProdQtyErr = !!prodErrors.quantity;
                    const isProdValErr = !!prodErrors.unitValue;
                    const isHSNErr = isExport && !prod.HSNCode?.trim();

                    return (
                      <Box
                        key={prod.id || prodIdx}
                        sx={{
                          border: '1px dashed #ccc',
                          borderRadius: '6px',
                          p: 1.5,
                          mb: 2,
                          bgcolor: '#ffffff',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#777' }}>
                            Product {prodIdx + 1}
                          </Typography>
                          {box.products.length > 1 && (
                            <IconButton color="error" size="small" onClick={() => removeProduct(boxIdx, prodIdx)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>

                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={4}>
                            <SbTextField
                              placeholder="Description"
                              label="Description"
                              required
                              name="description"
                              value={prod.description || ''}
                              onChange={e => handleProductChange(boxIdx, prodIdx, 'description', e.target.value)}
                              error={isProdDescErr ? prodErrors.description : ''}
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <SbTextField
                              placeholder="Quantity"
                              label="Quantity"
                              required
                              type="number"
                              name="quantity"
                              value={prod.quantity || ''}
                              onChange={e => handleProductChange(boxIdx, prodIdx, 'quantity', e.target.value)}
                              error={isProdQtyErr ? prodErrors.quantity : ''}
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <SbTextField
                              placeholder="Unit Value"
                              label="Unit Value"
                              required
                              type="number"
                              name="unitValue"
                              value={prod.unitValue || ''}
                              onChange={e => handleProductChange(boxIdx, prodIdx, 'unitValue', e.target.value)}
                              error={isProdValErr ? prodErrors.unitValue : ''}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <SbTextField
                              placeholder="HSN Code"
                              label="HSN Code"
                              required={isExport}
                              name="HSNCode"
                              value={prod.HSNCode || ''}
                              onChange={e => handleProductChange(boxIdx, prodIdx, 'HSNCode', e.target.value)}
                              error={isHSNErr ? 'Required for export' : ''}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          p: 2.5,
          px: 3,
          justifyContent: 'space-between',
          bgcolor: alpha(theme.palette.text.primary, 0.02),
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: '8px', px: 2.5, textTransform: 'none', fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveClick}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: '8px',
            px: 3.5,
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
